/** The pattern recommended by the specification, see https://semver.org/spec/v2.0.0.html. */
const SEMANTIC_VERSION_PATTERN = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/;

const NUMERIC_IDENTIFIER_PATTERN = /^\d+$/;

type TComparison = -1 | 0 | 1;

type TSemanticVersion = {
  major: number,
  minor: number,
  patch: number,
  preReleaseIdentifiers: string[],
};

export function isSemanticVersion(version: string): boolean {
  return SEMANTIC_VERSION_PATTERN.test(version);
}

function parseSemanticVersion(version: string): TSemanticVersion {
  const match = SEMANTIC_VERSION_PATTERN.exec(version);
  if (match === null) {
    throw new Error(`Invalid version "${version}", expected a semantic version, e.g. 2.10.0 or 2.10.0-rc.1.`);
  }
  const [
    ,
    major,
    minor,
    patch,
    preRelease,
  ] = match;
  return {
    major: parseInt(major, 10),
    minor: parseInt(minor, 10),
    patch: parseInt(patch, 10),
    preReleaseIdentifiers: preRelease === undefined ? [] : preRelease.split('.'),
  };
}

function compareNumbers(first: number, second: number): TComparison {
  if (first === second) {
    return 0;
  }
  return first > second ? 1 : -1;
}

/** Numeric identifiers compare as numbers and rank below alphanumeric ones, which compare in ASCII order. */
function comparePreReleaseIdentifiers(first: string, second: string): TComparison {
  const isFirstNumeric = NUMERIC_IDENTIFIER_PATTERN.test(first);
  const isSecondNumeric = NUMERIC_IDENTIFIER_PATTERN.test(second);
  if (isFirstNumeric && isSecondNumeric) {
    return compareNumbers(parseInt(first, 10), parseInt(second, 10));
  }
  if (isFirstNumeric !== isSecondNumeric) {
    return isFirstNumeric ? -1 : 1;
  }
  if (first === second) {
    return 0;
  }
  return first > second ? 1 : -1;
}

/** A version without pre-release ranks above its pre-releases, e.g. `2.10.0` above `2.10.0-rc.1`. */
function comparePreReleases(first: string[], second: string[]): TComparison {
  if (first.length === 0 || second.length === 0) {
    return compareNumbers(second.length, first.length);
  }
  const identifierCount = Math.min(first.length, second.length);
  for (let i = 0; i < identifierCount; i++) {
    const comparison = comparePreReleaseIdentifiers(first[i], second[i]);
    if (comparison !== 0) {
      return comparison;
    }
  }
  return compareNumbers(first.length, second.length);
}

/**
 * Compares semantic versions by their precedence as defined by SemVer 2.0.0, e.g. `2.10.0` is
 * greater than `2.9.3`, and `2.10.0-rc.1` is less than `2.10.0`. Build metadata, e.g. `+build.5`,
 * is ignored. Throws for anything that is not a semantic version.
 */
export function compareVersions(first: string, second: string): TComparison {
  const firstVersion = parseSemanticVersion(first);
  const secondVersion = parseSemanticVersion(second);
  return compareNumbers(firstVersion.major, secondVersion.major)
    || compareNumbers(firstVersion.minor, secondVersion.minor)
    || compareNumbers(firstVersion.patch, secondVersion.patch)
    || comparePreReleases(firstVersion.preReleaseIdentifiers, secondVersion.preReleaseIdentifiers);
}
